import { defineMessages } from 'react-intl'
import { messages as rightSidebarMessages } from 'features/recumbent/PlannerSidebar/RightSidebar'
import { titles as plannerOnboardingMessages } from 'features/recumbent/PlannerOnboardingModal/tips'
import { messages as routeToolsMessages } from 'features/recumbent/PlannerSidebar/RouteTools'
import { messages as clickActionsMessages } from 'features/recumbent/PlannerSidebar/ClickActions'
import { settingsMessages } from 'common/MapV2/controls/SettingsMenu'
import { commonNouns, commonVerbs } from 'lib/localization'

const messages = defineMessages({
  clickAndDrag: {
    id: 'totd.clickAndDrag',
    defaultMessage: 'When planning or viewing a route, click and drag on the elevation profile to zoom in on a specific section of your route. Click the Edit link that appears to access advanced tools.'
  },
  mapStyle: {
    id: 'totd.mapStyle',
    defaultMessage: "By changing the map style, you can switch between maps that focus on different information. Different styles use different routing data. If you get stuck while planning, or don't see a trail you want, switching your map style may help."
  },
  optimizeFor: {
    id: 'totd.optimizeFor',
    defaultMessage: 'Change the rules of the road by switching routing optimizations in the right hand sidebar. Don’t be afraid to try walking and driving modes, they can help route around obstructions. Click below to learn more about the different modes.'
  },
  combining: {
    id: 'totd.combining',
    defaultMessage: 'To join two routes together, click the Add New button at the top of the left sidebar. Once your desired route is added to the map, join them by clicking the green dot. You can route from the end of one to the start of another by clicking on the map like usual.'
  },
  splitting: {
    id: 'totd.splitting',
    defaultMessage: "To split a route, click and drag on the elevation profile then click the Edit link that appears. You'll be able to then remove the selected section. The remaining route sections can be saved as separate routes, or you can reconnect them in a different way."
  },
  addPoi: {
    id: 'totd.addPoi',
    defaultMessage: 'Add Points of Interest (POI) to your map by clicking the Add POI button in the right sidebar, then clicking anywhere on the map. POI are shown on printed maps and cuesheets, as well as our mobile app during navigation.'
  },
  changeColor: {
    id: 'totd.changeColor',
    defaultMessage: 'Change the color of a section of your route by clicking and dragging the elevation profile, then clicking the Change Color tool in the selection tools menu'
  },
  deleteSection: {
    id: 'totd.deleteSection',
    defaultMessage: 'To delete a section of your route, click and drag on the elevation profile on the section you wish to delete, then click Delete Selection. If you delete a selection in the middle of your route, you can re-join the remaining sections in a new way, or you can save them as separate routes.'
  },
  addRemoveCp: {
    id: 'totd.addRemoveCp',
    defaultMessage: 'You can click and drag your route to edit it. Mouse over the route, then click and drag the blue dot. Click below to learn how to control how much of your route changes when clicking and dragging.'
  },
  customCues: {
    id: 'totd.customCues',
    defaultMessage: 'Add custom cues by clicking the Add to Cuesheet button in the right sidebar, then clicking the blue dot seen while mousing along your route. These cues will be available on printed maps and when navigating using the Ride with GPS app, a Garmin unit, or a Wahoo ELEMNT.'
  },
  drawLines: {
    id: 'totd.drawLines',
    defaultMessage: 'Use the Draw Lines button in the right sidebar to switch from following roads and paths to drawing straight lines between clicks. This is helpful when working with trails that are not routable, or when dealing with unusual intersections or parking lots.'
  },
  seeMore: {
    id: 'totd.seeMore',
    defaultMessage: 'See more of your map by collapsing the right and left sidebars. Simply click the left and right arrow icons near the top of the sidebars to collapse them. Click the same icons to reopen them when you need to access route planning tools.'
  }
})

export const tips = [
  {
    tip: messages.clickAndDrag,
    link: 'https://ridewithgps.com/help/the-elevation-profile-for-routes'
  },
  {
    tip: messages.mapStyle,
    link: 'https://ridewithgps.com/help/switching-map-styles'
  },
  {
    tip: messages.optimizeFor,
    link: 'https://ridewithgps.com/help/route-planner#Routing-style'
  },
  {
    tip: messages.combining,
    link: 'https://ridewithgps.com/help/combining-and-splitting-routes#combining'
  },
  {
    tip: messages.splitting,
    link: 'https://ridewithgps.com/help/combining-and-splitting-routes#splitting'
  },
  {
    tip: messages.addPoi,
    link: 'https://ridewithgps.com/help/point-of-interest-pois#adding'
  },
  {
    tip: messages.changeColor,
    link: 'https://ridewithgps.com/help/edit-color-on-a-section-of-route'
  },
  {
    tip: messages.deleteSection,
    link: 'https://ridewithgps.com/help/the-elevation-profile-for-routes#edit'
  },
  {
    tip: messages.addRemoveCp,
    link: 'https://ridewithgps.com/help/route-planner#add-remove-control-points'
  },
  {
    tip: messages.customCues,
    link: 'https://ridewithgps.com/help/edit-cue-sheet'
  },
  {
    tip: messages.drawLines,
    link: 'https://ridewithgps.com/help/planning#drawlines'
  },
  {
    tip: messages.seeMore,
    link: null
  }
]

const hotkeyMessages = defineMessages({
  history: {
    id: 'hotkeyMessages.history',
    defaultMessage: 'History'
  },
  zoom: {
    id: 'hotkeyMessages.zoom',
    defaultMessage: 'Zoom'
  },
  fullZoom: {
    id: 'hotkeyMessages.fullZoom',
    defaultMessage: 'Full zoom'
  },
  zoomIn: {
    id: 'hotkeyMessages.zoomIn',
    defaultMessage: 'Zoom in'
  },
  zoomOut: {
    id: 'hotkeyMessages.zoomOut',
    defaultMessage: 'Zoom out'
  },
  addRoute: {
    id: 'hotkeyMessages.addRoute',
    defaultMessage: 'Add route'
  },
  clearMap: {
    id: 'hotkeyMessages.clearMap',
    defaultMessage: 'Clear map'
  },
  closeStreetView: {
    id: 'hotkeyMessages.closeStreetView',
    defaultMessage: 'Close Street View'
  },
  displayToggles: {
    id: 'hotkeyMessages.displayToggles',
    defaultMessage: 'Display Toggles'
  },
  toggleDistanceMarkers: {
    id: 'hotkeyMessages.toggleDistanceMarkers',
    defaultMessage: 'Toggle distance markers'
  },
  leftSidebar: {
    id: 'hotkeyMessages.leftSidebar',
    defaultMessage: 'Left sidebar'
  },
  rightSidebar: {
    id: 'hotkeyMessages.rightSidebar',
    defaultMessage: 'Right sidebar'
  },
  elevationProfile: {
    id: 'hotkeyMessages.elevationProfile',
    defaultMessage: 'Elevation profile'
  },
  clickMode: {
    id: 'hotkeyMessages.clickMode',
    defaultMessage: 'Click mode'
  }
})
hotkeyMessages.undo = rightSidebarMessages.undo
hotkeyMessages.redo = rightSidebarMessages.redo
hotkeyMessages.map = commonNouns.map
hotkeyMessages.save = commonVerbs.save
hotkeyMessages.cueIcons = settingsMessages.cueIcons
hotkeyMessages.distanceMarkers = settingsMessages.distanceMarkers
hotkeyMessages.followRoads = commonVerbs.followRoads
hotkeyMessages.drawLines = plannerOnboardingMessages.drawLines
hotkeyMessages.POI = routeToolsMessages.POI
hotkeyMessages.cuesheet = commonNouns.cuesheet
hotkeyMessages.controlPoint = clickActionsMessages.controlPoint

export const hotkeys = [[
  [hotkeyMessages.history],
  [['mod+z', 'u'], hotkeyMessages.undo],
  [['mod+y', 'mod+shift+z', 'r'], hotkeyMessages.redo],
  [hotkeyMessages.zoom],
  ['o', hotkeyMessages.fullZoom],
  ['-', hotkeyMessages.zoomIn],
  ['=', hotkeyMessages.zoomOut],
  [hotkeyMessages.map],
  [['n'], hotkeyMessages.addRoute],
  [['mod+del', 'x'], hotkeyMessages.clearMap],
  [['mod+s', 's'], hotkeyMessages.save],
  ['esc', hotkeyMessages.closeStreetView]
], [
  [hotkeyMessages.displayToggles],
  ['t c', hotkeyMessages.cueIcons],
  ['t d', hotkeyMessages.distanceMarkers],
  ['[', hotkeyMessages.leftSidebar],
  [']', hotkeyMessages.rightSidebar],
  ['\\', hotkeyMessages.elevationProfile],
  [hotkeyMessages.clickMode],
  ['m f', hotkeyMessages.followRoads],
  ['m d', hotkeyMessages.drawLines],
  ['m i', hotkeyMessages.POI],
  ['m c', hotkeyMessages.cuesheet],
  ['m p', hotkeyMessages.controlPoint]
]]
